import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'connected-react-router';
import { OrderedMap, OrderedSet, Set } from 'immutable';
import valid from 'card-validator';

import PaymentMethodForm from 'components/payment-methods/payment-method-form';
import ProgressButton from 'components/common/buttons/progress-button';

import PaymentMethodModel from 'models/payment-method';

import NewPaymentMethodModel from 'models/braintree/new-payment-method';

import getPaymentMethods from 'action-creators/payment-methods/get-payment-methods';
import setDefaultPaymentMethod from 'action-creators/payment-methods/set-default-payment-method';
import savePaymentMethodCreator from 'action-creators/payment-methods/save-payment-method';
import modifyRequiredFieldListCreator from 'action-creators/payment-methods/modify-required-field-list';
import setShouldScrollToErrorCreator from 'action-creators/payment-methods/set-should-scroll-to-error';
import braintreeFieldStateChange from 'action-creators/payment-methods/braintree-field-state-change';
import Brand from 'models/brand';
import AccountHeadline from 'components/common/account/account-headline';
import trackEventCreator from 'action-creators/analytics/track-event';

const propTypes = {
  paymentMethod: PropTypes.oneOfType([
    PropTypes.instanceOf(PaymentMethodModel),
    PropTypes.instanceOf(NewPaymentMethodModel),
  ]).isRequired,
  requiredFields: PropTypes.instanceOf(Set).isRequired,
  requiredFieldErrors: PropTypes.instanceOf(OrderedMap).isRequired,
  shouldScrollToError: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  savePaymentMethod: PropTypes.func.isRequired,
  braintreeFieldStateChange: PropTypes.func.isRequired,
  setShouldScrollToError: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  dismissPage: PropTypes.func.isRequired,
  app: PropTypes.string.isRequired,
  trackEvent: PropTypes.func.isRequired,

  // Optional, or async loaded properties
  braintreeToken: PropTypes.string,
  handleBraintreePaymentMethodError: PropTypes.func,
  brand: PropTypes.instanceOf(Brand).isRequired,
};

const defaultProps = {
  braintreeToken: null,
  handleBraintreePaymentMethodError: () => {},
};

class NewPaymentMethod extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dirty: false,
      cardValues: {
        number: null,
        expirationMonth: null,
        expirationYear: null,
        cvv: null,
        postalCode: null,
      },
    };

    this.dismissPage = this.dismissPage.bind(this);
    this.onFieldEvent = this.onFieldEvent.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidMount() {
    require('smoothscroll-polyfill').polyfill();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    const { braintreeToken } = this.props;

    if (braintreeToken) {
      import('braintree-web').then((braintree) => { this.initializeBraintree(braintree); }).catch((e) => { console.error(`Failed to initialize braintree: ${e}`); });
    }
  }

  componentDidUpdate(prevProps) {
    const { braintreeToken } = this.props;
    if (braintreeToken && !prevProps.braintreeToken) {
      import('braintree-web').then(braintree => this.initializeBraintree(braintree)).catch(e => console.error(`Failed to initialize braintree: ${e}`));
    }
  }

  componentWillUnmount() {
    if (this.btHandler) {
      this.btHandler.teardown(() => {
        this.btHandler = null;
      });
    }
  }

  onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    const { savePaymentMethod } = this.props;
    const { cardValues } = this.state;
    this.client.request({
      endpoint: 'payment_methods/credit_cards',
      method: 'post',
      data: {
        creditCard: {
          number: cardValues.number,
          cvv: cardValues.cvv,
          expirationMonth: cardValues.expirationMonth,
          expirationYear: cardValues.expirationYear,
          billingAddress: {
            postalCode: cardValues.postalCode,
          },
          options: {
            validate: false,
          },
        },
      },
    }, (requestErr, response) => {
      // More detailed example of handling API errors: https://codepen.io/braintree/pen/MbwjdM
      if (requestErr) {
        console.warn('err', requestErr);
      } else {
        const nicknameField = document.querySelector('#card-nickname');
        savePaymentMethod({ nonce: response.creditCards[0].nonce, label: nicknameField.value });
      }
    });
  }

  onFieldEvent(e) {
    const dataFieldName = e.target.getAttribute('data-field-name');
    let fieldValidity;
    if (dataFieldName === 'cvv'){
      fieldValidity = valid.cvv(e.target.value, 4)
    } else {
      fieldValidity = (valid[dataFieldName] ? valid[dataFieldName](e.target.value) : null);
    }
    if (!fieldValidity) { return; }

    const cardValues = this.state.cardValues;
    cardValues[dataFieldName] = e.target.value;
    this.setState({ cardValues, dirty: true });

    Object.assign(fieldValidity, {
      isEmpty: !e.target.value.length,
      target: {
        fieldKey: dataFieldName,
      },
    });

    this.props.braintreeFieldStateChange({ event: fieldValidity });
  }

  initializeBraintree(braintree) {
    const { braintreeToken } = this.props;
    braintree.client.create({
      authorization: braintreeToken,
    }, (clientErr, clientInstance) => {
      if (clientErr) {
        // TODO this might have to go in callback for subclient
        console.warn(JSON.stringify(clientErr));
        return;
      }
      this.client = clientInstance;
    });
  }

  dismissPage() {
    if (!this.state.dirty || window.confirm('Are you sure you want to dismiss your changes?')) {
      this.props.dismissPage();
    }
  }

  render() {
    const {
      paymentMethod,
      requiredFields,
      requiredFieldErrors,
      shouldScrollToError,
      savePaymentMethod,
      setShouldScrollToError,
      modifyRequiredFieldList,
      submitting,
      brand,
      app,
      trackEvent,
    } = this.props;

    return (
      <div>
        <AccountHeadline brand={brand} headline="MANAGE PAYMENT METHODS" app={app} trackEvent={trackEvent} />
        <div className="heavy-shadowed area dismissable">
          <div className="dismiss-button clickable" onClick={this.dismissPage} />
          <div className="row">
            <div className="col-md-10 col-md-offset-1 padding-0">
              <h4 className="h4-sg text-color-slate text-weight-medium with-bottom-margin-2x">ADD A PAYMENT METHOD</h4>
              <p className="text-weight-light with-bottom-margin-2x">
                Control is key. Your payment information is safe and secure with ParkWhiz.
                All of our payments are processed through Braintree, with the highest data security and fraud protection available.
              </p>
              <hr className="visible-xs basic-grey-divider with-bottom-margin-2x vertical-padding-bottom" />
              <div className="col-md-10 padding-0">
                <form id="payment-method-form" onSubmit={this.onSubmit}>
                  <PaymentMethodForm
                    newPaymentMethod={paymentMethod}
                    isPaypal={false}
                    creditCardFieldsClassNames="non-paypal"
                    onFieldEvent={this.onFieldEvent}
                    requiredFields={requiredFields}
                    requiredFieldErrors={requiredFieldErrors}
                    shouldScrollToError={shouldScrollToError}
                    setShouldScrollToError={setShouldScrollToError}
                    modifyRequiredFieldList={modifyRequiredFieldList}
                    saveCreditCard
                    hideSaveToggle
                    showNicknameField
                    showPaypal={false}
                  />
                  <div className="row">
                    <div className="col-md-6">
                      <ProgressButton
                        buttonClassName={'btn-block btn-primary text-center progress-btn-background-green'}
                        loading={submitting}
                        onClick={this.onSubmit}
                      >
                        <input type="submit" value="SAVE THIS CARD" />
                      </ProgressButton>
                    </div>
                    <div className="col-md-2 text-center">
                      <a
                        className="btn-link btn-block text-weight-medium button-centered-link"
                        onClick={this.dismissPage}
                      >
                        CANCEL
                      </a>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

NewPaymentMethod.propTypes = propTypes;
NewPaymentMethod.defaultProps = defaultProps;

const dismissPage = () => push('/account/payment-methods/');

const mapStateToProps = (state) => {
  const {
    paymentMethod,
    braintreeToken,
    requiredFields,
    requiredFieldErrors,
    shouldScrollToError,
    submitting,
  } = state.paymentMethods;
  const { name: app } = state.app;
  return {
    paymentMethod,
    braintreeToken,
    requiredFields,
    requiredFieldErrors,
    shouldScrollToError,
    submitting,
    app,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    getPaymentMethods,
    setDefaultPaymentMethod,
    dismissPage,
    modifyRequiredFieldList: modifyRequiredFieldListCreator,
    setShouldScrollToError: setShouldScrollToErrorCreator,
    braintreeFieldStateChange,
    savePaymentMethod: savePaymentMethodCreator,
    trackEvent: trackEventCreator,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(NewPaymentMethod);
